import React, { useMemo, useRef, useState } from "react"

// redux
import { mapDispatchProps, mapStateToProps } from "../../store/store"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"
import { ArrowDown } from "react-feather"
import { ROUTES } from "../../routes"
import { motion, useMotionValueEvent, useScroll } from "framer-motion"
import { APP_LOCALE, getLanguage, useLanguage } from "@languages/index"
import classNames from "classnames"
import { LOCALES } from "constants/locales"
import DOMAIN from "constants/domain"

const CampaignIntroCommon = (props: any) => {
  const navigate = useNavigate()
  const [showFloatingButtons, setShowFloatingButtons] = useState(false)
  const scrollContainerRef = useRef(null)
  const { scrollY } = useScroll({
    container: scrollContainerRef,
  })

  const languageCode = getLanguage()
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.campaigns }

  useMotionValueEvent(scrollY, "change", (latest) => {
    const nextState = latest > 740 ? true : false
    if (showFloatingButtons != nextState) {
      setShowFloatingButtons(nextState)
    }
  })

  const footerAnimVariants = {
    hidden: { opacity: 0, y: 300 },
    show: { opacity: 1, y: 0 },
  }

  const openPDF = () => {
    let url = "https://" + DOMAIN.BASE.URL + "/intro/seeding"
    window.open(url, "_blank")
  }

  const BaseView = (props) =>
    useMemo(() => {
      const HeaderChildren = (
        <div className="flex flex-col max-w-[1000px] w-full mx-auto">
          <div className="flex flex-col justify-center items-center mb-[20px] md:mb-[30px]">
            <span
              className={classNames({
                "text-white leading-tight tracking-tighter font-bold text-center": true,
                "text-[28px] md:text-[42px] lg:text-[56px]": languageCode === LOCALES.KO,
                "text-[22px] md:text-[42px] lg:text-[56px]": languageCode === LOCALES.JA,
              })}
            >
              {languageTexts._intro_common_text_1()}
            </span>
          </div>

          <div
            className={classNames({
              "mb-[25px] md:mb-[36px] text-white text-center": true,
              "text-[16px] md:text-[17px] lg:text-[18px]": languageCode === LOCALES.KO,
              "text-[14px] md:text-[16px] lg:text-[18px]": languageCode === LOCALES.JA,
            })}
          >
            <span>{languageTexts.intro_common_text_2}</span>
          </div>

          <div className="flex flex-row gap-[5px] mx-auto justify-center">
            <div
              className="w-[149px] h-14 rounded-[56px] border border-white flex justify-center items-center cursor-pointer"
              onClick={() => {
                openPDF()
              }}
            >
              <span className="text-base text-center text-white font-bold">{languageTexts.intro_common_text_3}</span>
            </div>
            <div
              className="w-[149px] h-14 bg-orange-500 rounded-[56px] flex justify-center items-center cursor-pointer"
              onClick={() => {
                navigate(ROUTES.CAMPAIGN_INDEX.PATH)
              }}
            >
              <span className="text-base text-center text-white font-bold">{languageTexts.intro_common_text_4}</span>
            </div>
          </div>
        </div>
      )

      return (
        <>
          <div
            className="section-1 w-full h-[500px] flex md:hidden flex-col items-center justify-center py-[80px] bg-cover bg-center bg-black"
            style={{
              backgroundImage: `url('/assets/images/seeding/seeding-intro-01-m_${APP_LOCALE}.png')`,
              boxShadow: "inset 0 0 0 1000px rgba(0,0,0,.3)",
            }}
          >
            {HeaderChildren}
          </div>
          <div
            className="section-1 w-full h-[680px] hidden md:flex flex-col items-center justify-center py-[80px] bg-cover bg-center bg-black"
            style={{
              backgroundImage: `url('/assets/images/seeding/seeding-intro-01_${APP_LOCALE}.png')`,
              boxShadow: "inset 0 0 0 1000px rgba(0,0,0,.3)",
            }}
          >
            {HeaderChildren}
          </div>

          <div className="w-full flex flex-col justify-center items-center mb-[100px] md:mb-[160px]">
            <img
              className="w-full h-full md:hidden"
              src={`/assets/images/seeding/seeding-intro-02-m_${languageCode}.png`}
            />
            <img
              className="w-full h-full hidden md:block"
              src={`/assets/images/seeding/seeding-intro-02_${languageCode}.png`}
            />
          </div>

          <div className="section-2 bg-white items-center justify-center py-[150px]">
            <div className="flex flex-col justify-center items-center max-w-[1024px] w-full mx-auto px-[20px] xl:p-0 gap-[150px]">
              <div className="checkpoint-1 flex flex-col">
                <span
                  className={classNames({
                    "mb-[15px] md:mb-[25px] text-[#000] font-bold leading-tight tracking-tighter": true,
                    "text-[28px] md:text-[38px] lg:text-[48px]": languageCode === LOCALES.KO,
                    "text-[20px] md:text-[32px] lg:text-[48px]": languageCode === LOCALES.JA,
                  })}
                >
                  {languageTexts._intro_common_text_5()}
                </span>

                <span
                  className={classNames({
                    "mb-[30px] md:mb-[50px] text-[#666]": true,
                    "text-[16px] md:text-[17px] lg:text-[18px]": languageCode === LOCALES.KO,
                    "text-[12px] md:text-[14px] lg:text-[18px]": languageCode === LOCALES.JA,
                  })}
                >
                  {languageTexts._intro_common_text_6()}
                </span>

                <div className="flex flex-col justify-center items-center w-full mx-auto mb-[100px] md:mb-[150px]">
                  <img
                    className="w-full h-full md:hidden"
                    src={`/assets/images/seeding/seeding-intro-03-m_${languageCode}.png`}
                  />
                  <img
                    className="w-full h-full hidden md:block"
                    src={`/assets/images/seeding/seeding-intro-03_${languageCode}.png`}
                  />
                </div>
              </div>

              <div className="checkpoint-2 flex flex-col">
                <span
                  className={classNames({
                    "mb-[15px] md:mb-[25px] text-[#000] font-bold leading-tight tracking-tighter": true,
                    "text-[28px] md:text-[38px] lg:text-[48px]": languageCode === LOCALES.KO,
                    "text-[24px] md:text-[32px] lg:text-[48px]": languageCode === LOCALES.JA,
                  })}
                >
                  {languageTexts._intro_common_text_7()}
                </span>

                <span
                  className={classNames({
                    "mb-[30px] md:mb-[50px] text-[#666]": true,
                    "text-[16px] md:text-[17px] lg:text-[18px]": languageCode === LOCALES.KO,
                    "text-[13px] md:text-[15px] lg:text-[18px]": languageCode === LOCALES.JA,
                  })}
                >
                  {languageTexts.intro_common_text_8}
                </span>

                <div className="flex flex-col justify-center items-center w-full mx-auto mb-[100px] md:mb-[150px]">
                  <img
                    className="w-full h-full md:hidden"
                    src={`/assets/images/seeding/seeding-intro-04-m_${languageCode}.png`}
                  />
                  <img
                    className="w-full h-full hidden md:block"
                    src={`/assets/images/seeding/seeding-intro-04_${languageCode}.png`}
                  />
                </div>
              </div>

              <div className="checkpoint-3 flex flex-col lg:flex-row lg:gap-[40px]">
                <div className="flex flex-col lg:min-w-[520px]">
                  <span
                    className={classNames({
                      "mb-[15px] md:mb-[25px] text-[#000] font-bold leading-tight tracking-tighter": true,
                      "text-[28px] md:text-[38px] lg:text-[48px]": languageCode === LOCALES.KO,
                      "text-[24px] md:text-[32px] lg:text-[43px]": languageCode === LOCALES.JA,
                    })}
                  >
                    {languageTexts._intro_common_text_9()}
                  </span>

                  <span
                    className={classNames({
                      "mb-[30px] md:mb-[50px] text-[#666]": true,
                      "text-[16px] md:text-[17px] lg:text-[18px]": languageCode === LOCALES.KO,
                      "text-[12px] md:text-[14px] lg:text-[18px]": languageCode === LOCALES.JA,
                    })}
                  >
                    {languageTexts._intro_common_text_10()}
                  </span>
                </div>

                <div className="flex flex-col justify-center items-center w-full mx-auto mb-[100px] md:mb-[150px]">
                  <img
                    className="w-full h-full md:hidden"
                    src={`/assets/images/seeding/seeding-intro-05-m_${languageCode}.png`}
                  />
                  <img
                    className="w-full h-full hidden md:block"
                    src={`/assets/images/seeding/seeding-intro-05_${languageCode}.png`}
                  />
                </div>
              </div>

              <div className="checkpoint-4 flex flex-col">
                <span
                  className={classNames({
                    "mb-[15px] md:mb-[25px] text-[#000] font-bold leading-tight tracking-tighter": true,
                    "text-[28px] md:text-[38px] lg:text-[48px]": languageCode === LOCALES.KO,
                    "text-[24px] md:text-[32px] lg:text-[48px]": languageCode === LOCALES.JA,
                  })}
                >
                  {languageTexts._intro_common_text_11()}
                </span>

                <span
                  className={classNames({
                    "mb-[30px] md:mb-[50px] text-[#666]": true,
                    "text-[16px] md:text-[17px] lg:text-[18px]": languageCode === LOCALES.KO,
                    "text-[12px] md:text-[14px] lg:text-[18px]": languageCode === LOCALES.JA,
                  })}
                >
                  {languageTexts._intro_common_text_12()}
                </span>

                <div className="flex flex-col justify-center items-center w-full mx-auto mb-[100px] md:mb-[150px]">
                  <img
                    className="w-full h-full md:hidden"
                    src={`/assets/images/seeding/seeding-intro-06-m_${languageCode}.png`}
                  />
                  <img
                    className="w-full h-full hidden md:block"
                    src={`/assets/images/seeding/seeding-intro-06_${languageCode}.png`}
                  />
                </div>
              </div>

              <div className="checkpoint-5 flex flex-col">
                <span
                  className={classNames({
                    "mb-[15px] md:mb-[25px] text-[#000] font-bold leading-tight tracking-tighter": true,
                    "text-[28px] md:text-[38px] lg:text-[48px]": languageCode === LOCALES.KO,
                    "text-[24px] md:text-[32px] lg:text-[48px]": languageCode === LOCALES.JA,
                  })}
                >
                  {languageTexts._intro_common_text_13()}
                </span>

                <span
                  className={classNames({
                    "mb-[30px] md:mb-[50px] text-[#666]": true,
                    "text-[16px] md:text-[17px] lg:text-[18px]": languageCode === LOCALES.KO,
                    "text-[12px] md:text-[14px] lg:text-[18px]": languageCode === LOCALES.JA,
                  })}
                >
                  {languageTexts.intro_common_text_14}
                </span>

                <div className="flex flex-col justify-center items-center w-full mx-auto mb-[100px] md:mb-[150px]">
                  <img
                    className="w-full h-full md:hidden"
                    src={`/assets/images/seeding/seeding-intro-07-m_${languageCode}.png`}
                  />
                  <img
                    className="w-full h-full hidden md:block"
                    src={`/assets/images/seeding/seeding-intro-07_${languageCode}.png`}
                  />
                </div>
              </div>

              <div className="checkpoint-6 flex flex-col">
                <span
                  className={classNames({
                    "mb-[15px] md:mb-[25px] text-[#000] font-bold leading-tight tracking-tighter": true,
                    "text-[28px] md:text-[38px] lg:text-[48px]": languageCode === LOCALES.KO,
                    "text-[24px] md:text-[32px] lg:text-[48px]": languageCode === LOCALES.JA,
                  })}
                >
                  {languageTexts._intro_common_text_15()}
                </span>

                <span className="text-[16px] md:text-[17px] lg:text-[18px] text-[#666] mb-[30px] md:mb-[50px]">
                  {languageTexts._intro_common_text_16()}
                </span>

                <div className="flex flex-col justify-center items-center w-full mx-auto mb-[100px] md:mb-[150px]">
                  <img
                    className="w-full h-full md:hidden"
                    src={`/assets/images/seeding/seeding-intro-08-m_${languageCode}.png`}
                  />
                  <img
                    className="w-full h-full hidden md:block"
                    src={`/assets/images/seeding/seeding-intro-08_${languageCode}.png`}
                  />
                </div>
              </div>

              {APP_LOCALE === LOCALES.KO && (
                <div className="checkpoint-7 flex flex-col">
                  <span className="text-[28px] md:text-[38px] lg:text-[48px] text-[#000] font-bold mb-[15px] md:mb-[25px] leading-tight tracking-tighter">
                    {languageTexts._intro_common_text_17()}
                  </span>

                  <span className="text-[16px] md:text-[17px] lg:text-[18px] text-[#666] mb-[30px] md:mb-[50px]">
                    {languageTexts.intro_common_text_18}
                  </span>

                  <div className="flex flex-col justify-center items-center w-full mx-auto mb-[100px] md:mb-[150px]">
                    <img className="w-full h-full md:hidden" src={`/assets/images/seeding/seeding-intro-09-m.png`} />
                    <img
                      className="w-full h-full hidden md:block"
                      src={`/assets/images/seeding/seeding-intro-09.png`}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )
    }, [props])

  return (
    <div className="relative select-none">
      <BaseView />

      <motion.div
        className="fixed bottom-0 left-0 right-0 md:mb-[40px]"
        variants={footerAnimVariants}
        initial={"hidden"}
        animate={showFloatingButtons ? "show" : "hidden"}
        transition={{ type: "just" }}
      >
        <div className="flex flex-row cursor-pointer mt-[-1px] justify-center items-center border-t border-[#E8E8E8] md:border-0 md:gap-[12px]">
          <div
            className="bg-[#000000] flex flex-1 flex-col justify-center items-center md:rounded-full md:max-w-[248px]  break-keep max-h-[60px]"
            style={{
              boxShadow: "0px 10px 20px 0px rgba(0, 0, 0, 0.25)",
            }}
            onClick={() => {
              openPDF()
            }}
          >
            <span className="text-[16px] font-bold text-center text-[#FFF] px-[40px] py-[18px]">
              {languageTexts.intro_common_text_19}
            </span>
          </div>
          <div
            className="bg-[#FF0099] flex flex-1 flex-col justify-center items-center md:rounded-full md:max-w-[248px]"
            style={{
              boxShadow: "0px 10px 20px 0px rgba(0, 0, 0, 0.25)",
            }}
            onClick={() => {
              navigate(ROUTES.CAMPAIGN_INDEX.PATH)
            }}
          >
            <span className="text-[16px] font-bold text-center text-[#FFF] px-[40px] py-[18px]">
              {languageTexts.intro_common_text_20}
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(CampaignIntroCommon)
